/**
 * Feito pelo agente netexperts
 * Módulo: AnalyticsObra — tabela de drill-down paginada (read-only)
 * Data: 2026-06-14
 *
 * Sempre paginada via backend (page/per_page). Nunca acumula páginas no estado.
 * Linhas expansíveis (vendas): carrega detalhe sob demanda, 1 aberta por vez, cache LRU.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { ChevronLeftIcon, ChevronRightIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline'
import FormBusca from '../../../../Components/Forms/Busca'
import { useObraDetalhes } from '../hooks/useObraAnalytics'
import VendaContratoExpansao from './VendaContratoExpansao'
import EstoqueUnidadeExpansao from './EstoqueUnidadeExpansao'

const CACHE_MAX = 5

const DrillDownTable = ({ obraId, area, investidor, config, period, showSearch = true }) => {
    const { data, loading, fetchDetalhes } = useObraDetalhes(obraId, area, investidor)
    const [searchTerm, setSearchTerm] = useState('')
    const [selectedFilterField, setSelectedFilterField] = useState(config.filterOptions?.[0]?.value || '')
    const [selectedSortField, setSelectedSortField] = useState(config.sortOptions?.[0]?.value || 'data')
    const [sortDirection, setSortDirection] = useState('desc')
    const [recordsPerPage, setRecordsPerPage] = useState(10)
    const [page, setPage] = useState(0)
    const [expandedKey, setExpandedKey] = useState(null)
    const expansaoCache = useRef(new Map())

    const expandLabel = area === 'estoque' ? 'Expandir unidade' : area === 'a-receber' ? 'Expandir contrato' : 'Expandir venda'
    const expandLabelClose = area === 'estoque' ? 'Recolher unidade' : area === 'a-receber' ? 'Recolher contrato' : 'Recolher venda'
    const isExpandable = Boolean(config.expandable && config.rowKey)
    const rowKeyField = config.rowKey || 'id'
    const expandIdKey = config.expandIdKey

    const getExpandKey = (row, idx) => {
        if (expandIdKey != null && row[expandIdKey] != null) return `id:${row[expandIdKey]}`
        const fallback = row[rowKeyField]
        if (fallback != null && fallback !== '') return `${rowKeyField}:${fallback}`
        return `row:${page}:${idx}`
    }

    const cacheGet = (key) => expansaoCache.current.get(key)
    const cacheSet = useCallback((key, value) => {
        const cache = expansaoCache.current
        if (cache.size >= CACHE_MAX && !cache.has(key)) {
            const oldest = cache.keys().next().value
            cache.delete(oldest)
        }
        cache.set(key, value)
    }, [])

    const load = (overrides = {}) => {
        const params = {
            page: overrides.page ?? 0,
            per_page: overrides.recordsPerPage ?? recordsPerPage,
            sort: overrides.selectedSortField ?? selectedSortField,
            sort_dir: overrides.sortDirection ?? sortDirection,
            filter: overrides.selectedFilterField ?? selectedFilterField,
            search: overrides.searchTerm !== undefined ? overrides.searchTerm : searchTerm,
            inicio: period?.inicio,
            fim: period?.fim,
        }
        fetchDetalhes(params)
    }

    useEffect(() => {
        setPage(0)
        setExpandedKey(null)
        load({ page: 0 })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [obraId, area, period?.inicio, period?.fim])

    const search = (formData) => {
        setPage(0)
        setExpandedKey(null)
        load({ ...formData, page: 0 })
    }

    const handleClear = () => {
        setSearchTerm('')
        setSelectedFilterField(config.filterOptions[0].value)
        setSelectedSortField(config.sortOptions[0].value)
        setSortDirection('desc')
        setRecordsPerPage(10)
        setPage(0)
        setExpandedKey(null)
        load({ page: 0, recordsPerPage: 10, selectedSortField: config.sortOptions[0].value, sortDirection: 'desc', selectedFilterField: config.filterOptions[0].value, searchTerm: '' })
    }

    const goTo = (newPage) => {
        setPage(newPage)
        setExpandedKey(null)
        load({ page: newPage })
    }

    const toggleExpand = (row, idx) => {
        const key = getExpandKey(row, idx)
        setExpandedKey((prev) => (prev === key ? null : key))
    }

    const rows = data?.rows ?? []
    const totalPages = data?.total_pages ?? 0
    const Icon = config.icon

    return (
        <div className="space-y-4">
            {config.subtitle && (
                <p className="text-xs text-gray-500 px-1">{config.subtitle}</p>
            )}

            {showSearch && config.filterOptions?.length > 0 && (
                <FormBusca
                    filterOptions={config.filterOptions}
                    sortOptions={config.sortOptions}
                    search={search}
                    handleClear={handleClear}
                    color="yellow"
                    haveData={rows.length > 0}
                    items={{ searchTerm, setSearchTerm, selectedFilterField, setSelectedFilterField, selectedSortField, setSelectedSortField, sortDirection, setSortDirection, recordsPerPage, setRecordsPerPage }}
                />
            )}

            <div data-netexperts="true" className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <div className="p-1.5 bg-gray-900 rounded-lg">
                            <Icon className="w-4 h-4 text-brand-yellow" />
                        </div>
                        <h2 className="text-sm font-semibold text-gray-900">{config.title}</h2>
                    </div>
                    <span className="text-xs text-gray-400">{data?.total ?? 0} registros (dados reais)</span>
                </div>

                <div className="overflow-x-auto">
                    <div className="min-w-[860px]">
                        <div
                            className="grid gap-2 px-4 py-2 bg-gray-50 border-b border-gray-100 text-xs font-semibold text-gray-500 uppercase tracking-wide"
                            style={{ gridTemplateColumns: isExpandable ? `40px repeat(${config.columns.length}, minmax(0, 1fr))` : `repeat(${config.columns.length}, minmax(0, 1fr))` }}
                        >
                            {isExpandable && <div />}
                            {config.columns.map((col) => (
                                <div key={col.key} className="text-center">{col.label}</div>
                            ))}
                        </div>
                        <div className="divide-y divide-gray-50">
                            {loading ? (
                                <div className="px-4 py-10 text-center text-sm text-gray-400">Carregando…</div>
                            ) : rows.length === 0 ? (
                                <div className="px-4 py-10 text-center text-sm text-gray-400">Nenhum registro encontrado.</div>
                            ) : (
                                rows.map((row, idx) => {
                                    const expandKey = getExpandKey(row, idx)
                                    const isOpen = expandedKey === expandKey
                                    return (
                                        <div key={expandKey}>
                                            <div
                                                className={`grid gap-2 px-4 py-3 transition-colors ${isOpen
                                                        ? (area === 'estoque' ? 'bg-emerald-50/50' : area === 'a-receber' ? 'bg-amber-50/50' : 'bg-blue-50/50')
                                                        : row.devedor
                                                            ? 'bg-rose-50/40 hover:bg-rose-50/60'
                                                            : 'hover:bg-gray-50'
                                                    }`}
                                                style={{ gridTemplateColumns: isExpandable ? `40px repeat(${config.columns.length}, minmax(0, 1fr))` : `repeat(${config.columns.length}, minmax(0, 1fr))` }}
                                            >
                                                {isExpandable && (
                                                    <div className="flex items-center justify-center">
                                                        <button
                                                            type="button"
                                                            onClick={() => toggleExpand(row, idx)}
                                                            className="p-1 rounded-lg text-gray-500 hover:bg-gray-200 hover:text-gray-800 transition-colors"
                                                            title={isOpen ? expandLabelClose : expandLabel}
                                                            aria-expanded={isOpen}
                                                        >
                                                            {isOpen
                                                                ? <ChevronUpIcon className="w-4 h-4" />
                                                                : <ChevronDownIcon className="w-4 h-4" />}
                                                        </button>
                                                    </div>
                                                )}
                                                {config.columns.map((col) => {
                                                    const raw = row[col.key]
                                                    const display = col.format ? col.format(raw) : (raw ?? '-')
                                                    return (
                                                        <div key={col.key} className="text-center text-xs text-gray-600 truncate" title={String(raw ?? '-')}>
                                                            {display}
                                                        </div>
                                                    )
                                                })}
                                            </div>
                                            {isExpandable && isOpen && area === 'vendas' && (
                                                <VendaContratoExpansao
                                                    obraId={obraId}
                                                    contrato={row[rowKeyField]}
                                                    investidor={investidor}
                                                    enabled={isOpen}
                                                    cached={cacheGet(expandKey)}
                                                    onLoaded={(k, res) => cacheSet(expandKey, res)}
                                                />
                                            )}
                                            {isExpandable && isOpen && area === 'a-receber' && (
                                                <VendaContratoExpansao
                                                    obraId={obraId}
                                                    contrato={row[rowKeyField]}
                                                    investidor={investidor}
                                                    enabled={isOpen}
                                                    cached={cacheGet(expandKey)}
                                                    onLoaded={(k, res) => cacheSet(expandKey, res)}
                                                />
                                            )}
                                            {isExpandable && isOpen && area === 'estoque' && (
                                                <EstoqueUnidadeExpansao
                                                    obraId={obraId}
                                                    row={row}
                                                    investidor={investidor}
                                                    period={period}
                                                    enabled={isOpen}
                                                    cacheKey={expandKey}
                                                    cached={cacheGet(expandKey)}
                                                    onLoaded={cacheSet}
                                                />
                                            )}
                                        </div>
                                    )
                                })
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {rows.length > 0 && (
                <div className="flex items-center justify-between bg-white border border-gray-100 rounded-2xl px-5 py-3 shadow-sm">
                    <span className="text-sm text-gray-500">Página {page + 1} de {Math.max(1, totalPages)}</span>
                    <div className="flex gap-2">
                        <button onClick={() => goTo(page - 1)} disabled={page === 0} className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                            <ChevronLeftIcon className="w-4 h-4" /> Anterior
                        </button>
                        <button onClick={() => goTo(page + 1)} disabled={page + 1 >= totalPages} className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                            Próximo <ChevronRightIcon className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            )}
        </div>
    )
}

export default DrillDownTable
